import { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import errorReporter from '@app/errorReporter';
import { message } from 'components';
import { AdminContext } from 'contexts';
import type {
  IUserGetTransfersResponse as TransfersDTO,
  IUserGetOngoingTransferCountResponse as CountDTO,
} from '@/app-package';
import type { TransferSortBy } from '@bb/users';
import { TransferState, TransferType } from '@bb/users';
import { getTransfers, getOngoingTransferCount } from 'api/UserController';
import { useDebounce } from 'hooks';
import { PAGE_SIZE } from '../Tables/constants';

interface UseGetTransfersProps {
  shouldFetch?: boolean;
  pageNumber?: number;
  stateFilters: TransferState[];
  typeFilters?: TransferType[];
  searchText?: string;
  sort?: string;
  isDesc?: boolean;
}

const NUM_TRANSFER_TYPES = Object.keys(TransferType).length;

const useGetTransfers = ({
  shouldFetch = true,
  pageNumber,
  searchText,
  stateFilters,
  typeFilters,
  sort,
  isDesc,
}: UseGetTransfersProps) => {
  const { ongoingTransferCount: initialOngoingCount, isOngoingCountLoaded } =
    useContext(AdminContext);
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);
  const [transfers, setTransfers] = useState<TransfersDTO['transfers']>([]);
  const [transferCount, setTransferCount] = useState<number>(null);
  const [ongoingCount, setOngoingCount] = useState<number>(
    isOngoingCountLoaded ? initialOngoingCount : null,
  );
  const debouncedSearchText = useDebounce(searchText);
  const lastFetchTimestampRef = useRef<number>(0);

  const fetchTransfers = useCallback(async () => {
    try {
      setIsLoading(true);
      lastFetchTimestampRef.current = Date.now();
      const currentFetchTimestamp = lastFetchTimestampRef.current;

      const transferPromise = getTransfers({
        type: typeFilters,
        state: stateFilters,
        page: pageNumber - 1,
        name: debouncedSearchText,
        limit: PAGE_SIZE,
        sortBy: sort as TransferSortBy,
        order: isDesc ? 'desc' : 'asc',
      });

      let countPromise;
      if (
        stateFilters.length &&
        !stateFilters.includes(TransferState.Pending)
      ) {
        // Pending transfer are filtered out
        countPromise = Promise.resolve({ count: 0 });
      } else {
        countPromise = getOngoingTransferCount({
          name: debouncedSearchText,
          type: typeFilters,
        });
      }

      const [transferResponse, ongoingCountResponse] = (await Promise.all([
        transferPromise,
        countPromise,
      ])) as [TransfersDTO, CountDTO];

      if (currentFetchTimestamp !== lastFetchTimestampRef.current) return;

      setTransfers(transferResponse.transfers);
      setTransferCount(transferResponse.count);
      setOngoingCount(ongoingCountResponse.count);

      const searchParams = new URLSearchParams(history.location.search);
      searchParams.delete('transferStateFilters[]');
      searchParams.delete('transferTypeFilters[]');
      if (stateFilters.length) {
        stateFilters.forEach(filter =>
          searchParams.append('transferStateFilters[]', filter),
        );
      }
      if (typeFilters?.length) {
        typeFilters.forEach(filter =>
          searchParams.append('transferTypeFilters[]', filter),
        );
      }
      searchParams.set('transfersSort', sort || 'status');
      searchParams.set('transfersOrder', isDesc ? 'desc' : 'asc');
      searchParams.set('pageNumber', pageNumber.toString());
      searchParams.set('userName', debouncedSearchText);
      history.replace({
        pathname: history.location.pathname,
        search: searchParams.toString(),
      });
    } catch (err) {
      errorReporter.error(err);
      message.error('Error loading transfers');
    } finally {
      setIsLoading(false);
    }
  }, [
    pageNumber,
    stateFilters,
    typeFilters,
    sort,
    isDesc,
    debouncedSearchText,
    history,
  ]);

  useEffect(() => {
    if (!shouldFetch) return;
    fetchTransfers();
    return () => {
      lastFetchTimestampRef.current = 0;
    };
  }, [shouldFetch, fetchTransfers]);

  useEffect(() => {
    if (isOngoingCountLoaded && ongoingCount === null) {
      setOngoingCount(initialOngoingCount);
    }
  }, [isOngoingCountLoaded, ongoingCount, initialOngoingCount]);

  useEffect(() => {
    // Respond to socket events on initial counter but only if
    // it's not the active tab and no ongoing transfers would be filtered
    const allOngoingTransfersIncluded =
      (stateFilters.length === 0 ||
        stateFilters.includes(TransferState.Pending)) &&
      (typeFilters.length === 0 || typeFilters.length === NUM_TRANSFER_TYPES) &&
      !searchText;
    if (!shouldFetch && allOngoingTransfersIncluded) {
      setOngoingCount(initialOngoingCount);
    }
  }, [initialOngoingCount, stateFilters, typeFilters, shouldFetch, searchText]);

  return {
    isLoading,
    transfers,
    fetchTransfers,
    transferCount,
    ongoingCount,
    setTransfers,
  };
};

export default useGetTransfers;
